import { CloseCircleFilled, ExclamationCircleFilled, ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { MsgModalModel, useKeyboardModalProps } from "@org-avp/avp-avengers-ui-shared-components";
import { Modal, ModalProps } from "antd";
import { ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colorError, colorInfo, colorWarning, detail, iconImg, message, messageContainer, texts, title } from "./styles";

const ConfirmationIcon = <ExclamationCircleOutlined style={{ ...iconImg, ...colorWarning }}/>;
const ErrorIcon = <CloseCircleFilled style={{ ...iconImg, ...colorError }}/>;
const InfoIcon = <InfoCircleOutlined style={{ ...iconImg, ...colorInfo }}/>;
const WarningIcon = <ExclamationCircleFilled style={{ ...iconImg, ...colorWarning }}/>;

const renderIcon = (msgModalData?: MsgModalModel) => {
  if (!msgModalData) return <></>;
  switch (msgModalData.type) {
    case "CONFIRMATION":
      return ConfirmationIcon;
    case "ERROR":
      return ErrorIcon;
    case "INFO":
      return InfoIcon;
    case "WARNING":
      return WarningIcon;
  }
};

const noneCancelButtonProps = { style: { display: "none", ...texts } };
const cancelButtonProps = { style: {} };

export const useMessageDialog = <T, >(
  msgModalModel: MsgModalModel,
  onApply?: (data?: T) => void,
  onCancel?: () => void,
) => {
  const { t } = useTranslation(["common"]);

  const modalProps: ModalProps = useMemo(
    () => ({
      cancelButtonProps: msgModalModel?.type !== "CONFIRMATION" ? noneCancelButtonProps : cancelButtonProps,
      cancelText: msgModalModel?.cancelText ?? t("cancel"),
      centered: true,
      closeIcon: false,
      okText: msgModalModel?.okText ?? t("ok"),
      width: msgModalModel?.width,
    }),
    [msgModalModel?.cancelText, msgModalModel?.okText, msgModalModel?.type, msgModalModel?.width, t],
  );

  const keyboardModalProps = useKeyboardModalProps(modalProps);

  const openDialogWithData = useCallback((data: T | undefined, messageNode?: ReactNode) => {
    keyboardModalProps.onCancel = () => onCancel && onCancel();
    keyboardModalProps.onOk = () => onApply && onApply(data);
    // workaround for type issue in antd: width field definition differs between ModalProps and ModalFuncProps
    const width = keyboardModalProps.width as string | number | undefined;
    Modal.confirm({
      style: texts,
      icon: renderIcon(msgModalModel),
      content: (
        <div style={messageContainer}>
          <div style={texts}>
            <div style={title}>{msgModalModel?.title}</div>
            <div style={message}>{messageNode ? messageNode : msgModalModel?.message}</div>
          </div>
          <div style={detail}>{msgModalModel?.detail}</div>
        </div>
      ),
      ...keyboardModalProps,
      width,
    });
  }, [keyboardModalProps, msgModalModel, onApply, onCancel]);

  const openDialog = useCallback(() => {
    openDialogWithData(undefined, undefined);
  }, [openDialogWithData]);

  const openDialogWithMessage = useCallback((messageNode: ReactNode) => {
    openDialogWithData(undefined, messageNode);
  }, [openDialogWithData]);

  return useMemo(() => ({
    openDialog, openDialogWithMessage, openDialogWithData,
  }), [openDialog, openDialogWithData, openDialogWithMessage]);
};
